import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui';

/**
 * What was said in a recording, under the player.
 *
 * The dashboard transcribes each segment as it lands (services/transcripts.ts
 * on the server) and keeps them together in <prefix>/transcripts/index.json.
 * Each line's time there is seconds into ITS SEGMENT; the recording's timeline
 * is the manifest's segment durations end to end, so a line's place in the
 * player is its segment's start plus that - the same timeline the scrub bar,
 * markers and downloads use. Wall-clock times add the gaps where the feed
 * dropped, exactly as the multiviewer's own page does.
 *
 * Reading it works as the multiviewer's page does (docs/tools/recordings.md,
 * "The transcript panel"): a click plays from a line; a drag picks whole
 * lines, Shift+click stretches the pick; Copy or Ctrl+C turns the pick into a
 * quote with where and when it was said; Esc or a click elsewhere lets go.
 */

interface Line {
  s: number;
  e: number;
  text: string;
}

interface SegmentTranscript {
  tag: string;
  start: number;
  lines: Line[];
  error?: string;
  /** A recorder's chunk: the manifest parts it covers. */
  parts?: string[];
}

interface Row {
  t: number;
  e: number;
  part: number;
  text: string;
}

export interface TranscriptSegment {
  tag: string;
  seconds: number;
  gapBefore?: number;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 01:02:03 - hours always shown, so a column lines up. */
const tc = (s: number) => {
  const t = Math.max(0, Math.floor(s));
  return `${pad2(Math.floor(t / 3600))}:${pad2(Math.floor((t % 3600) / 60))}:${pad2(t % 60)}`;
};

/** 2:12:23 or 12:23 - the player's own counter, for quotes. */
const fmt = (s: number) => {
  const t = Math.max(0, Math.floor(s));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
  return (h ? `${h}:${pad2(m)}` : `${m}`) + `:${pad2(sec)}`;
};

export function sessionMs(stamp: string): number | null {
  const m = stamp.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})$/);
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) : null;
}

const clock = (ms: number) => {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

/** "2026-09-26, 2:59pm" in this computer's timezone. */
const dateAmPm = (ms: number) => {
  const d = new Date(ms);
  const h = d.getHours();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}, ${h % 12 || 12}:${pad2(d.getMinutes())}${h < 12 ? 'am' : 'pm'}`;
};

/**
 * Wall-clock time of a point in a recording, in epoch ms. Counts the gaps
 * where the feed dropped, and what a trim cut from the front.
 */
export function wallClock(session: string, segments: TranscriptSegment[], headOffset: number, t: number): number | null {
  const base = sessionMs(session);
  if (base == null) return null;
  let at = 0, gaps = 0;
  for (const seg of segments) {
    if (at > t) break;
    gaps += seg.gapBefore ?? 0;
    at += seg.seconds;
  }
  return base + (t + gaps + headOffset) * 1000;
}

/** "Cam1: 2026-09-26, 2:59pm." - the recording, and when a moment in it happened. */
export function whereLabel(name: string, wall: number | null): string {
  return wall != null ? `${name}: ${dateAmPm(wall)}.` : `${name}.`;
}

/** Copy, with the old fallback for a browser that has no clipboard API in this context. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      const done = document.execCommand('copy');
      document.body.removeChild(ta);
      return done;
    } catch {
      return false;
    }
  }
}

export function RecordingTranscript({
  prefix,
  name,
  session,
  segments,
  position,
  recording = false,
  headOffset = 0,
  onSeek,
  onPresence,
}: {
  prefix: string;
  /** What the recording is called, for the head of a copied quote. */
  name: string;
  session: string;
  segments: TranscriptSegment[];
  position: number;
  /** Still being recorded or transcribed: a transcript may yet appear, so keep looking. */
  recording?: boolean;
  /** Seconds cut from the front by a trim, so times of day still agree with the clock. */
  headOffset?: number;
  onSeek: (t: number) => void;
  /** Told whether a transcript exists at all (null while finding out). */
  onPresence?: (has: boolean | null) => void;
}) {
  const [data, setData] = useState<SegmentTranscript[] | null>(null);
  // No transcript exists for this recording (nothing has been transcribed, or
  // transcription is off on this hub). The panel is then not shown at all,
  // rather than sitting there saying "still transcribing" for ever.
  const [none, setNone] = useState(false);
  const [query, setQuery] = useState('');
  const [follow, setFollow] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  // Lines picked: the row a drag started on, and where it is now (indexes into `shown`).
  const [sel, setSel] = useState<{ a: number; b: number } | null>(null);
  // While the mouse is down on a line: where it started, whether it has moved, where it is.
  const drag = useRef<{ a: number; moved: boolean; y: number } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Re-read while open: a recording still being made gains a segment's worth
  // every five minutes or so.
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setNone(false);
    setSel(null);
    const load = async () => {
      try {
        const { url } = await api.get<{ url: string }>(
          `/recordings/url?key=${encodeURIComponent(`${prefix}/transcripts/index.json`)}`,
        );
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) {
          if (!cancelled) { setNone(true); setData((d) => d ?? []); }
          return;
        }
        const body = (await res.json()) as { segments?: SegmentTranscript[] };
        if (!cancelled) { setNone(false); setData(body.segments ?? []); }
      } catch {
        if (!cancelled) { setNone(true); setData((d) => d ?? []); }
      }
    };
    void load();
    const timer = setInterval(() => void load(), 20_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [prefix]);

  /** Where each segment starts on the recording timeline, and its gaps so far. */
  const starts = useMemo(() => {
    const map = new Map<string, { start: number; gaps: number; part: number }>();
    let t = 0;
    let gaps = 0;
    segments.forEach((seg, i) => {
      gaps += seg.gapBefore ?? 0;
      map.set(seg.tag, { start: t, gaps, part: i + 1 });
      t += seg.seconds;
    });
    return map;
  }, [segments]);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const seg of data ?? []) {
      // A segment newer than the manifest this page loaded still places
      // correctly, from the offset the server recorded when it transcribed it.
      const at = starts.get(seg.tag);
      const start = at?.start ?? seg.start;
      const part = at?.part ?? Number(seg.tag.replace(/\D/g, ''));
      for (const l of seg.lines) out.push({ t: start + l.s, e: start + (l.e ?? l.s), part, text: l.text });
    }
    return out.sort((a, b) => a.t - b.t);
  }, [data, starts]);

  // "Has a transcript" means words, or parts transcribed without error. An
  // index holding nothing but failures (every part of an encoder recording,
  // before those were done in runs) is no transcript, so the page offers to make one.
  const hasWords = rows.length > 0 || (data ?? []).some((s) => !s.error && s.lines.length > 0);
  useEffect(() => { onPresence?.(data == null ? null : !none && hasWords); }, [data, none, hasWords, onPresence]);

  const wallAt = useCallback((t: number) => wallClock(session, segments, headOffset, t), [session, segments, headOffset]);

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => (q ? rows.filter((r) => r.text.toLowerCase().includes(q)) : rows), [rows, q]);

  // The line being spoken now: the last one that has started.
  let active = -1;
  for (let i = 0; i < shown.length && shown[i].t <= position; i++) active = i;

  // Keep the line in view while following - but not while lines are picked,
  // or the list would scroll out from under the pick.
  useEffect(() => {
    if (!follow || active < 0 || sel) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row="${active}"]`);
    const box = listRef.current;
    if (el && box) box.scrollTop = el.offsetTop - box.clientHeight / 3;
  }, [active, follow, sel]);

  const picked = useMemo(() => (sel ? shown.slice(Math.min(sel.a, sel.b), Math.max(sel.a, sel.b) + 1) : []), [sel, shown]);

  /**
   * Lines picked, as one paragraph - ready to drop into a document as a quote:
   *   Cam1: 2026-09-26, 2:59pm. 2:12:23 - 2:24:05
   *   everything said, run together
   * The date and time of day are where the first line starts; the times after
   * are in the recording, from the first line's start to the last one's end.
   */
  const quote = useCallback((list: Row[]) => {
    const first = list[0], last = list[list.length - 1];
    return `${whereLabel(name, wallAt(first.t))} ${fmt(first.t)} - ${fmt(last.e)}\n${list.map((r) => r.text.trim()).filter(Boolean).join(' ')}\n`;
  }, [name, wallAt]);

  /**
   * Plain text, one line each and tab separated, so it pastes into a document
   * as lines and into a spreadsheet as three columns.
   */
  const asText = useCallback((list: Row[], header: boolean) => {
    const body = list
      .map((r) => {
        const wall = wallAt(r.t);
        return `${tc(r.t)}\t${wall != null ? clock(wall) : ''}\t${r.text}`;
      })
      .join('\n');
    return (header ? 'Time in recording\tTime of day\tText\n' : '') + body + '\n';
  }, [wallAt]);

  const flash = useCallback((ok: boolean, n: number) => {
    setCopied(ok ? `copied ${n} line${n === 1 ? '' : 's'}` : 'copy failed');
    setTimeout(() => setCopied(null), ok ? 1500 : 3000);
  }, []);

  // The lines picked, as a quote; with none picked, what is listed -
  // everything, or just the search matches - a line each.
  const copy = useCallback(async () => {
    if (picked.length) { flash(await copyText(quote(picked)), picked.length); return; }
    if (!shown.length) return;
    flash(await copyText(asText(shown, false)), shown.length);
  }, [picked, shown, quote, asText, flash]);

  /** The row under a point, or the first or last one when above or below the list. */
  const rowAt = useCallback((y: number) => {
    const box = listRef.current;
    if (!box) return -1;
    const els = box.querySelectorAll<HTMLElement>('tr[data-row]');
    if (!els.length) return -1;
    const r = box.getBoundingClientRect();
    y = Math.min(Math.max(y, r.top + 1), r.bottom - 1);
    for (const el of els) if (y < el.getBoundingClientRect().bottom) return Number(el.dataset.row);
    return Number(els[els.length - 1].dataset.row);
  }, []);

  // On the document, so a drag carries on past the edge of the list (which
  // scrolls it, to reach lines out of view), and so Esc, Ctrl+C and a click
  // anywhere else work on the pick.
  useEffect(() => {
    const move = (e: MouseEvent) => {
      const d = drag.current;
      if (!d) return;
      d.y = e.clientY;
      const i = rowAt(e.clientY);
      if (i < 0 || (!d.moved && i === d.a)) return;
      d.moved = true;
      setSel({ a: d.a, b: i });
    };
    const up = () => {
      const d = drag.current;
      if (!d) return;
      drag.current = null;
      if (d.moved) return;
      // A plain click: play from that line, and let go of anything picked.
      setSel(null);
      const row = shown[d.a];
      if (row) onSeek(row.t);
    };
    const down = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const t = e.target as HTMLElement | null;
      if (listRef.current?.contains(t) || t?.closest('[data-tx-copy]')) return;
      setSel(null);
    };
    const key = (e: KeyboardEvent) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test((document.activeElement as HTMLElement | null)?.tagName ?? '')) return;
      if (e.key === 'Escape') { setSel(null); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C') && picked.length) {
        e.preventDefault();
        void copy();
      }
    };
    const edge = setInterval(() => {
      const d = drag.current;
      const box = listRef.current;
      if (!d?.moved || !box) return;
      const r = box.getBoundingClientRect();
      let step = 0;
      if (d.y < r.top + 24) step = -Math.min(40, r.top + 24 - d.y);
      else if (d.y > r.bottom - 24) step = Math.min(40, d.y - (r.bottom - 24));
      if (!step) return;
      box.scrollTop += step;
      const i = rowAt(d.y);
      if (i >= 0) setSel((s) => (s && s.b !== i ? { ...s, b: i } : s));
    }, 50);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => {
      clearInterval(edge);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.removeEventListener('mousedown', down);
      document.removeEventListener('keydown', key);
    };
  }, [shown, onSeek, rowAt, picked.length, copy]);

  // A record covers its own part, or (a recorder's chunk) a run of parts.
  const done = new Set((data ?? []).flatMap((s) => s.parts ?? [s.tag]));
  const pending = segments.filter((s) => !done.has(s.tag)).length;
  const failed = (data ?? []).filter((s) => s.error);

  // Nothing to show, and nothing on its way: a finished recording with no
  // transcript stays a player. One still running is looked at again, quietly.
  // (After every hook above, so React sees the same hooks on every render.)
  if (data == null || (none && !recording)) return null;
  // An index with no words in it (every part failed, or nothing said yet) is
  // no transcript either: the recording stays a player, not a list of parts.
  if (rows.length === 0 && !(recording && hasWords)) return null;

  function download() {
    const blob = new Blob([asText(rows, true)], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${prefix.split('/').slice(-2).join('_')}_transcript.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  const lo = sel ? Math.min(sel.a, sel.b) : -1;
  const hi = sel ? Math.max(sel.a, sel.b) : -2;
  const copyLabel = copied ?? (picked.length ? `Copy ${picked.length} line${picked.length === 1 ? '' : 's'}` : 'Copy');

  return (
    <div className="space-y-2 border-t border-grid pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Transcript</span>
        <span className="text-xs text-ink-muted">
          {`${rows.length} line${rows.length === 1 ? '' : 's'}` +
            (pending ? ` · ${pending} part${pending === 1 ? '' : 's'} still transcribing` : '')}
        </span>
        <div className="flex-1" />
        <input
          value={query}
          onChange={(e) => { setSel(null); setQuery(e.target.value); }}
          placeholder="search"
          className="w-full rounded border border-grid bg-plane px-2 py-1 text-xs sm:w-40"
        />
        <label className="flex cursor-pointer items-center gap-1 text-xs text-ink-muted">
          <input
            type="checkbox"
            className="accent-series-1"
            checked={follow}
            onChange={(e) => setFollow(e.target.checked)}
          />
          follow
        </label>
        <span data-tx-copy>
          <Button variant="ghost" disabled={!shown.length} onClick={() => void copy()}
                  title="Drag across lines to pick them, then Copy or Ctrl+C: a quote with where and when it was said. With none picked, copies every line listed">
            {copyLabel}
          </Button>
        </span>
        <Button variant="ghost" disabled={!rows.length} onClick={download}>
          Download .txt
        </Button>
      </div>

      {failed.length > 0 && (
        <p className="text-xs text-warning" title={failed.map((f) => `part ${starts.get(f.tag)?.part ?? f.tag}: ${f.error}`).join('\n')}>
          {failed.length === 1
            ? `Part ${starts.get(failed[0].tag)?.part ?? failed[0].tag} could not be transcribed.`
            : `${failed.length} parts could not be transcribed.`}
        </p>
      )}

      <div ref={listRef} className="relative max-h-72 overflow-auto rounded border border-grid">
        {shown.length === 0 && (
          <div className="px-3 py-6 text-center text-xs text-ink-muted">
            {q
              ? 'Nothing matches.'
              : pending
                ? 'Each part is transcribed a few minutes after it is recorded.'
                : 'No speech in this recording.'}
          </div>
        )}
        {/* Whole lines are picked, not letters (select-none): a drag picks rows. */}
        <table className="w-full select-none text-xs">
          <tbody>
            {shown.map((r, i) => {
              const wall = wallAt(r.t);
              const isSel = i >= lo && i <= hi;
              return (
                <tr
                  key={`${r.t}-${i}`}
                  data-row={i}
                  onMouseDown={(e) => {
                    if (e.button !== 0) return;
                    e.preventDefault(); // no letters selected, and focus off the search box
                    (document.activeElement as HTMLElement | null)?.blur?.();
                    if (e.shiftKey) {
                      setSel((s) => ({ a: s ? s.a : (active >= 0 ? active : i), b: i }));
                      return;
                    }
                    drag.current = { a: i, moved: false, y: e.clientY };
                  }}
                  className={`cursor-pointer border-b border-grid/50 hover:bg-raised ${
                    isSel ? 'bg-series-1/30' : i === active ? 'bg-series-1/15' : ''
                  }`}
                  title={`part ${r.part}`}
                >
                  <td className="whitespace-nowrap px-2 py-1 align-top tabular-nums">{tc(r.t)}</td>
                  <td className="hidden whitespace-nowrap px-2 py-1 align-top tabular-nums text-ink-muted sm:table-cell">
                    {wall != null ? clock(wall) : ''}
                  </td>
                  <td className="px-2 py-1">{r.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink-muted">
        Click a line to play from it. Drag across lines to pick them, then Copy or Ctrl+C for a quote with its
        time; Shift+click stretches the pick, Esc lets go. Times of day are in this computer&apos;s timezone.
      </p>
    </div>
  );
}
